import { useMemo } from 'react';
import { DEFAULT_SPEED } from './constants';

/**
 * Creates a mapping between process roles and their speeds from the roles array
 * @param {Array} roles - Array of role objects define in admin settings
 * @returns {Object} Mapping object where keys are process role names and values are speeds
 */
export const createRoleSpeedMapping = (roles) => {
  const mapping = {};

  if (!roles || !Array.isArray(roles)) {
    return mapping;
  }

  roles.forEach((role) => {
    if (role && role.role.name) {
      // Use the role's speed if available, otherwise default to DEFAULT_SPEED
      mapping[role.role.name] = role.role.speed || DEFAULT_SPEED;
    }
  });

  return mapping;
};

/**
 * Creates a memoized mapping between process roles and their speeds
 * @param {Array} roles - Array of roles
 * @returns {Object} Memoized mapping object
 */
export const useRoleSpeedMapping = (roles) => {
  return useMemo(() => createRoleSpeedMapping(roles), [roles]);
};

/**
 * Gets the speed for a specific process role from the roles array
 * @param {Array} roles - Array of role objects
 * @param {string} processRole - The process role name to find speed for
 * @returns {string} The speed for the process role, or DEFAULT_SPEED if not found
 */
export const getSpeedFromRoles = (roles, processRole) => {
  if (!roles || !Array.isArray(roles) || !processRole) {
    return DEFAULT_SPEED;
  }

  const role = roles.find((role) => role?.role?.name === processRole);
  return role?.role?.speed || DEFAULT_SPEED;
};
